import { useEffect, useState } from 'react';
import { buyCustomDomain, getDeploy, publishOutcome, type DeployStatus } from '@/api';
import { deployHost } from '../outputFiles';
import HostingPaywall from './HostingPaywall';

type Props = {
  slug?: string | null;
  outcomeId?: string | null;
};

export default function DeployUpsell({ slug = null, outcomeId = null }: Props) {
  const host = deployHost(slug);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [paywall, setPaywall] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [deploy, setDeploy] = useState<DeployStatus | null>(null);
  const [customDomain, setCustomDomain] = useState('');

  useEffect(() => {
    if (!outcomeId || !open) return;
    getDeploy(outcomeId).then(setDeploy).catch(() => undefined);
  }, [outcomeId, open]);

  const publish = async () => {
    if (!outcomeId) {
      setErr('Save the playbook before publishing');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const res = await publishOutcome(outcomeId);
      if (res.url) {
        setCheckoutUrl(res.url);
        setPaywall(true);
        return;
      }
      if (res.deploy) setDeploy(res.deploy);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'publish failed';
      if (msg.includes('start hosting') || msg.includes('Payment Required')) {
        setPaywall(true);
      } else {
        setErr(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        data-testid="deploy-upsell"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 bg-surface2 hover:bg-border text-textp text-xs font-medium px-3 py-1.5 rounded-md whitespace-nowrap"
      >
        <i className="ri-rocket-line" /> Publish
      </button>
      {open && (
        <div
          data-testid="deploy-upsell-panel"
          className="absolute right-0 top-full mt-2 z-20 w-80 bg-surface border border-border rounded-lg p-3 shadow-lg"
        >
          <div className="text-sm font-semibold text-textp">Host at {deploy?.hostname || host}</div>
          <p className="text-[12px] text-texts mt-1.5 leading-relaxed">
            Publishing applies isolated Terraform for this slug only. Platform apex, www, and infra stay untouched.
          </p>
          {deploy?.phase && deploy.phase !== 'idle' && (
            <p className="mt-2 text-[12px] font-mono text-textp" data-testid="deploy-phase">
              {deploy.phase}
              {deploy.error ? `: ${deploy.error}` : ''}
            </p>
          )}
          {err && <p className="mt-2 text-[12px] text-error">{err}</p>}
          {paywall ? (
            <div className="mt-2">
              {checkoutUrl ? (
                <a href={checkoutUrl} className="text-[12px] text-accent hover:underline">Continue to checkout</a>
              ) : (
                <HostingPaywall variant="panel" />
              )}
            </div>
          ) : (
            <button
              type="button"
              data-testid="deploy-publish"
              disabled={busy}
              onClick={() => void publish()}
              className="mt-3 text-[11px] text-accent hover:underline disabled:opacity-60"
            >
              {busy ? 'Publishing…' : 'Publish'}
            </button>
          )}
          {deploy?.phase === 'live' && (
            <div className="mt-3 pt-3 border-t border-border">
              <div className="text-[11px] text-texts">Custom domain (own hosted zone)</div>
              <input
                data-testid="deploy-custom-domain"
                value={customDomain}
                onChange={(e) => setCustomDomain(e.target.value)}
                placeholder="example.com"
                className="mt-1 w-full bg-surface2 border border-border rounded px-2 py-1 text-[12px] text-textp"
              />
              <button
                type="button"
                data-testid="deploy-buy-domain"
                disabled={busy || !customDomain.trim()}
                onClick={() => {
                  if (!outcomeId) return;
                  setBusy(true);
                  void buyCustomDomain(customDomain.trim(), outcomeId)
                    .then((res) => {
                      if (res.url) window.location.assign(res.url);
                      else void publishOutcome(outcomeId, { custom_domain: customDomain.trim() }).then((r) => {
                        if (r.deploy) setDeploy(r.deploy);
                      });
                    })
                    .catch((e) => setErr(e instanceof Error ? e.message : 'domain failed'))
                    .finally(() => setBusy(false));
                }}
                className="mt-2 text-[11px] text-accent hover:underline disabled:opacity-60"
              >
                Add custom domain
              </button>
              {deploy.name_servers && deploy.name_servers.length > 0 && (
                <p className="mt-2 text-[11px] font-mono text-texts">NS: {deploy.name_servers.join(' ')}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
